import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, RevealOnView } from '@/components/ui';
import { REVEAL_TIMING, useInView } from '@/utils/animation';

const ENTRY_KEYS = ['1990', '2004', '2008', '2013', '2016', 'now'] as const;

const HEADER_FADE_MS = 800;
const RAIL_DURATION_MS = 500;
const RAIL_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
// Rail draw starts halfway through the header fade rather than after it,
// so the whole timeline sequence reads quicker.
const RAIL_DELAY_MS = Math.round(HEADER_FADE_MS * 0.5);
const FIRST_ROW_DELAY_MS = RAIL_DELAY_MS + Math.round(RAIL_DURATION_MS * 0.5);
const ROW_STAGGER_MS = 150;
// Timeline rows use a shorter slide than the site-wide REVEAL_TIMING so the
// staggered sequence doesn't drag.
const ROW_DURATION_MS = 700;

export function RoadmapSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const visible = useInView(sectionRef);

  const fadeOffsetMs = Math.round(ROW_DURATION_MS * 0.3);
  const opacityDurationMs = ROW_DURATION_MS - fadeOffsetMs;

  return (
    <section
      ref={sectionRef}
      aria-labelledby="roadmap-heading"
      className="bg-clearlab-oat px-6 py-30 md:px-20 md:py-50"
    >
      <div className="mx-auto flex w-full max-w-content-865 flex-col gap-20 md:gap-30">
        <h2
          id="roadmap-heading"
          style={{
            opacity: visible ? 1 : 0,
            transition: `opacity ${HEADER_FADE_MS}ms ease-out`,
          }}
          className="text-center text-heading-32 font-normal tracking-tight text-clearlab-midnight"
        >
          {t('home.roadmap.heading')}
        </h2>

        <ol className="relative flex flex-col gap-20">
          <span
            aria-hidden
            style={{
              transform: visible ? 'scaleY(1)' : 'scaleY(0)',
              transformOrigin: 'top',
              transition: `transform ${RAIL_DURATION_MS}ms ${RAIL_EASE} ${RAIL_DELAY_MS}ms`,
            }}
            className="pointer-events-none absolute bottom-3 left-[5px] top-3 w-px bg-gradient-to-b from-clearlab-blue from-[90%] to-clearlab-oat to-[100%] md:left-[86px] lg:left-[270px] xl:left-[307px]"
          />

          {ENTRY_KEYS.map((entryKey, idx) => {
            const rowDelayMs = FIRST_ROW_DELAY_MS + idx * ROW_STAGGER_MS;
            const year = t(`home.roadmap.entries.${entryKey}.year`);
            const title = t(`home.roadmap.entries.${entryKey}.title`);
            const body = t(`home.roadmap.entries.${entryKey}.body`, {
              returnObjects: true,
            }) as string[];
            return (
              <li
                key={entryKey}
                style={{
                  transform: visible ? 'translateY(0)' : `translateY(${REVEAL_TIMING.offsetPx}px)`,
                  opacity: visible ? 1 : 0,
                  transition: `transform ${ROW_DURATION_MS}ms ${REVEAL_TIMING.ease} ${rowDelayMs}ms, opacity ${opacityDurationMs}ms ${REVEAL_TIMING.ease} ${rowDelayMs + fadeOffsetMs}ms`,
                }}
                className="relative flex items-start gap-5 md:gap-10 lg:justify-center lg:gap-[32px] xl:justify-start"
              >
                <p className="hidden shrink-0 text-left text-heading-16 font-semibold tracking-tight text-clearlab-steel/40 md:block md:w-10 lg:hidden">
                  {year}
                </p>

                <div className="hidden shrink-0 items-start lg:flex lg:w-60 lg:gap-[32px] xl:w-[313px]">
                  <p className="flex-1 text-right text-heading-16 font-semibold tracking-tight text-clearlab-steel/40">
                    {year}
                  </p>
                  <span className="mt-2.5 size-3 shrink-0 rounded-full border border-clearlab-blue bg-clearlab-oat" />
                </div>

                <span className="mt-2.5 size-3 shrink-0 rounded-full border border-clearlab-blue bg-clearlab-oat lg:hidden" />

                <div className="ml-auto flex max-w-[520px] flex-1 flex-col gap-2 lg:ml-0 xl:ml-auto">
                  <p className="text-heading-16 font-semibold tracking-tight text-clearlab-steel/40 md:hidden">
                    {year}
                  </p>
                  <h3 className="text-heading-16 font-bold tracking-tight text-clearlab-midnight md:max-w-60">
                    {title}
                  </h3>
                  {body.map((paragraph, i) => (
                    <p key={i} className="text-body-16 text-clearlab-midnight">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </li>
            );
          })}
        </ol>

        <RevealOnView from="left" fadeStart={0.3}>
          <div className="flex flex-col md:pl-[132px] lg:flex-row lg:items-start lg:justify-center lg:gap-[32px] lg:pl-0 xl:justify-start">
            <div aria-hidden className="hidden shrink-0 lg:block lg:w-60 xl:w-[313px]" />
            <div className="lg:max-w-[520px] lg:flex-1 xl:ml-auto">
              <Button to="/products" variant="outlined" size="large" tone="blue" iconRight>
                {t('home.roadmap.cta')}
              </Button>
            </div>
          </div>
        </RevealOnView>
      </div>
    </section>
  );
}
